import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Button } from "react-bootstrap";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import User from "../services/user";
import Auth from "../services/auth";
import { useQuery } from "../common/Hooks";
import LandingPage from "./components/LandingPage";
import TextBox from "../components/controls/form/TextBox";
import Deployment from "../services/deployment";

const USER_MANAGEMENT_TOKEN_TYPES = ["verify", "reset-password", "start-trial"];

const Verify = () => {
    const query = useQuery();
    const [translate] = useTranslation();
    const history = useHistory();

    const token = query.get("token");
    const type = query.get("type");

    const [userEmail, setUserEmail] = useState("");
    const [isVerifying, setIsVerifying] = useState(true);
    const [isVerified, setIsVerified] = useState(false);
    const [verificationMessage, setVerificationMessage] = useState("");
    const [errorSubCode, setErrorSubCode] = useState("");
    const [deploymentId, setDeploymentId] = useState(null);

    useEffect(() => {
        setUserEmail(query.get("email") || "");
    }, [query]);

    useEffect(() => {
        if (token && type && USER_MANAGEMENT_TOKEN_TYPES.includes(type)) {
            if (['verify', 'start-trial'].includes(type)) {
                User.verify({ token, startTrial: type === 'start-trial' }).then((res) => {
                    if (res.error) {
                        setVerificationMessage(res.data.message);
                        setErrorSubCode(res.data.subCode);
                        setDeploymentId(null);
                    }
                    else if (res.data.isPasswordSet) {
                        history.push(`/auth?email=${res.data.userEmail}`);
                    }
                    else {
                        setDeploymentId(res.data.deploymentId);
                        setVerificationMessage("OTM_VERIFICATION_SET_PASSWORD");
                        setErrorSubCode("");
                        setIsVerified(true);
                        setUserEmail(res.data.userEmail);
                    }
                }).finally(() => {
                    setIsVerifying(false);
                });
            }
            else {
                setVerificationMessage("OTM_VERIFICATION_SET_PASSWORD");
                setErrorSubCode("");
                setIsVerified(true);
                setIsVerifying(false);
            }
        }
        else {
            setIsVerifying(false);
            setVerificationMessage("OTM_VERIFICATION_MISSING_TOKEN");
            setErrorSubCode("");
        }
    }, [token, type, history]);

    const login = useCallback(async ({ email, password }) => {
        const res = await Auth.loginToken({ email, password, token, type });
        if (type === 'start-trial' && deploymentId) {
            Deployment.deploy(deploymentId);
        }
        if (!res.error) {
            history.push("/");
        }
        else {
            setVerificationMessage(res.data.message);
            setErrorSubCode(res.data.subCode);
        }
    }, [token, type, deploymentId, history]);

    const loginForm = useMemo(() => !isVerified ? null : (
        <Formik
            initialValues={{
                email: userEmail,
                password: "",
                confirmPassword: ""
            }}
            validationSchema={Yup.object({
                password: Yup.string().min(4, translate("OTM_CHARACTERS_OR_MORE", { minCharacters: 4 })).required(" "),
                confirmPassword: Yup.string().oneOf([Yup.ref("password")], translate("OTM_PASSWORD_SHOULD_MATCH")).required(" ")
            })}
            onSubmit={login}
        >
            <Form
                className="col-12 mt-2"
            >
                <TextBox
                    name="email"
                    disabled={true}
                    aria-label="email"
                />
                <TextBox
                    label={translate("OTM_PASSWORD")}
                    type="password"
                    name="password"
                    css="mt-2"
                />
                <TextBox
                    label={translate("OTM_CONFIRM_PASSWORD")}
                    type="password"
                    name="confirmPassword"
                    css="mt-2"
                />
                <Button
                    type="submit"
                    className="mt-4 d-flex mx-auto"
                >
                    {translate("OTM_LOGIN")}
                </Button>
            </Form>
        </Formik>
    ), [userEmail, isVerified, login, translate]);

    return (
        <LandingPage>
            <div className="row mt-n2">
                {isVerifying ? (
                    <div className="col-12 mt-2 d-flex flex-column align-items-center">
                        <div className="spinner-border" />
                        <span className="mt-2 fw-bold">{translate("OTM_VERIFYING_USER")}</span>
                    </div>
                ) : null}
                <div className="col-12 mt-2 text-center">
                    {translate(verificationMessage)}
                </div>
                {errorSubCode ? <div className="col-12 mt-2 text-center">
                    {translate(errorSubCode)}
                </div> : null}
                {loginForm}
            </div>
        </LandingPage>
    );
};

export default Verify;
